import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export default function ProfilePage() {
  const navigate = useNavigate();
  
  const getCache = (key, defaultVal) => {
    const cached = sessionStorage.getItem(`profile_${key}`);
    return cached ? JSON.parse(cached) : defaultVal;
  };

  const [user, setUser] = useState(() => getCache('user', {}));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState(() => getCache('activeTab', 'general'));
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(() => {
    const cachedUser = getCache('user', {});
    return { ...cachedUser, password: '' };
  });

  const isStaff = user.role === 'staff';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!sessionStorage.getItem('profile_user')) setLoading(true);
        const { data } = await API.get('/users/profile');
        setUser(data);
        setForm({ ...data, password: '' });
        sessionStorage.setItem('profile_user', JSON.stringify(data));
      } catch (err) {
        toast.error('Failed to load profile');
      } finally { setLoading(false); }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    sessionStorage.setItem('profile_activeTab', JSON.stringify(activeTab));
  }, [activeTab]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await API.put('/users/profile', form);
      setUser(data.user);
      setForm({ ...data.user, password: '' });
      sessionStorage.setItem('profile_user', JSON.stringify(data.user));
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);
      const { data } = await API.post('/users/profile/picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const updatedUser = { ...user, profilePicture: data.profilePicture };
      setUser(updatedUser);
      sessionStorage.setItem('profile_user', JSON.stringify(updatedUser));
      toast.success('Photo updated');
    } catch (err) { toast.error('Upload failed'); } 
    finally { setUploadingImage(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '20px' }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid #e0f2fe', borderTop: '3px solid #1880c2', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#64748b' }}>Loading Profile...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const MenuItem = ({ icon, label, active, onClick, isLink }) => (
    <button className={`menu-item ${active ? 'active' : ''} ${isLink ? 'link-item' : ''}`} onClick={onClick}>
      <span className="menu-icon">{icon}</span><span className="menu-label">{label}</span>{isLink && <span className="menu-arrow">→</span>}
    </button>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="profile-container">
        <div className="profile-cover"></div>
        <div className="content-wrapper">
          
          <aside className="sidebar">
            <div className="user-card">
              <div className="avatar-wrapper">
                <div className="avatar">
                  {user.profilePicture ? <img src={user.profilePicture} alt="User" /> : user.name?.charAt(0).toUpperCase()}
                  {uploadingImage && <div className="avatar-loader" />}
                </div>
                <button className="camera-btn" onClick={() => fileInputRef.current?.click()} title="Change Photo">📷</button>
                <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </div>
              <h2 className="user-name">{user.name}</h2>
              <p className="user-role">{user.pharmacyName || 'PharmaLedger Admin'}</p>
              <div className={`status-badge ${isStaff ? 'staff' : 'admin'}`}>
                <span className="dot"></span> {isStaff ? 'Staff Member' : 'Admin / Owner'}
              </div>
            </div>

            <nav className="side-menu">
              <div className="menu-group">
                <div className="menu-title">SETTINGS</div>
                <MenuItem icon="👤" label="Personal Info" active={activeTab === 'general'} onClick={() => setActiveTab('general')} />
                <MenuItem icon="🏥" label="Pharmacy Details" active={activeTab === 'business'} onClick={() => setActiveTab('business')} />
                <MenuItem icon="🔒" label="Security" active={activeTab === 'security'} onClick={() => setActiveTab('security')} />
              </div>

              {!isStaff && (
                <div className="menu-group">
                  <div className="menu-title">MANAGEMENT</div>
                  <MenuItem icon="👥" label="Staff Management" isLink onClick={() => navigate('/staff')} />
                  <MenuItem icon="📋" label="Activity Logs" isLink onClick={() => navigate('/activity-logs')} />
                </div>
              )}

              <div className="menu-group">
                <div className="menu-title">SUPPORT</div>
                <MenuItem icon="📞" label="Contact Us" isLink onClick={() => navigate('/contact')} />
              </div>
            </nav>
          </aside>

          <main className="main-panel">
            <div className="panel-header">
              <h1 className="panel-title">
                {activeTab === 'general' && 'Personal Information'}
                {activeTab === 'business' && 'Pharmacy Details'}
                {activeTab === 'security' && 'Security Settings'}
              </h1>
              <p className="panel-desc">Update your account settings and preferences.</p>
            </div>

            <div className="panel-body">
              {activeTab === 'general' && (
                <div className="form-grid">
                  <div className="input-group"><label>Full Name</label><input name="name" value={form.name || ''} onChange={handleChange} /></div>
                  <div className="input-group"><label>Phone Number</label><input name="phone" value={form.phone || ''} onChange={handleChange} /></div>
                  <div className="input-group full">
                    <label>Email Address</label>
                    <input name="email" value={form.email || ''} disabled className="disabled" />
                    <span className="hint">Email cannot be changed manually. Please contact support.</span>
                  </div>
                </div>
              )}

              {activeTab === 'business' && (
                <div className="form-grid">
                  {isStaff && (
                    <div className="info-box warning full">
                      <span className="icon">🔒</span>
                      <p><strong>Read Only:</strong> Staff members cannot edit pharmacy details. Contact your Admin to update.</p>
                    </div>
                  )}

                  <div className="input-group">
                    <label>Pharmacy Name</label>
                    <input 
                      name="pharmacyName" 
                      value={form.pharmacyName || ''} 
                      onChange={handleChange} 
                      disabled={isStaff}
                      className={isStaff ? 'disabled' : ''} 
                    />
                  </div>
                  <div className="input-group">
                    <label>PAN Number</label>
                    <input 
                      name="pan" 
                      value={form.pan || ''} 
                      onChange={handleChange} 
                      maxLength={9} 
                      disabled={isStaff}
                      className={isStaff ? 'disabled' : ''} 
                    />
                  </div>
                  <div className="input-group full">
                    <label>Pharmacy Address / Location</label>
                    <input 
                      name="address" 
                      value={form.address || ''} 
                      onChange={handleChange} 
                      placeholder="e.g. New Road, Kathmandu"
                      disabled={isStaff}
                      className={isStaff ? 'disabled' : ''} 
                    />
                    <span className="hint">This address prints on your official sales invoices and audit reports.</span>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="form-grid">
                  <div className="input-group full">
                    <label>New Password</label>
                    <input type="password" name="password" value={form.password || ''} onChange={handleChange} placeholder="••••••••" />
                    <span className="hint">Leave blank if you don't want to change your current password.</span>
                  </div>
                </div>
              )}

              <div className="panel-footer">
                <button className="btn-save" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

const styles = `
  .profile-container { 
    min-height: calc(100vh - 70px); 
    background-color: #f4f9fd;
    font-family: 'Inter', sans-serif; 
    color: #1a202c; 
    padding-bottom: 40px; 
  }
  
  .profile-cover { 
    height: 160px; 
    background: linear-gradient(135deg, #1880c2 0%, #0ea5e9 100%); 
    width: 100%; 
  }
  
  .content-wrapper { 
    max-width: 1100px; 
    margin: -80px auto 0; 
    padding: 0 20px; 
    display: grid; 
    grid-template-columns: 280px 1fr; 
    gap: 24px; 
    align-items: start; 
  }
  
  .user-card, .side-menu, .main-panel { 
    background: white; 
    border-radius: 12px; 
    border: 1px solid #e0f2fe; 
    box-shadow: 0 4px 16px rgba(24, 128, 194, 0.08); 
  }
  
  .user-card { 
    padding: 30px 20px; 
    text-align: center; 
    margin-bottom: 24px; 
  }
  
  .avatar-wrapper { position: relative; width: 90px; height: 90px; margin: 0 auto 16px; }
  .avatar { 
    width: 100%; height: 100%; 
    border-radius: 50%; 
    background: #e0f2fe; 
    color: #1880c2;
    border: 4px solid white; 
    display: flex; align-items: center; justify-content: center; 
    font-size: 32px; font-weight: 800;
    overflow: hidden; 
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  }
  .avatar img { width: 100%; height: 100%; object-fit: cover; }
  .camera-btn { 
    position: absolute; bottom: 0; right: 0; 
    background: #1880c2; color: white; 
    border: 2px solid white; width: 32px; height: 32px; border-radius: 50%; 
    cursor: pointer; display: flex; align-items: center; justify-content: center; 
    font-size: 14px; transition: 0.2s;
  }
  .camera-btn:hover { background: #0ea5e9; transform: scale(1.05); }
  
  .user-name { font-size: 18px; font-weight: 700; margin: 0; color: #1a202c; }
  .user-role { font-size: 13px; color: #64748b; margin: 4px 0 12px; }
  
  .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700; white-space: nowrap; }
  .status-badge.admin { background: #e0f2fe; color: #1880c2; border: 1px solid #bae6fd; }
  .status-badge.staff { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); color: #b45309; border: 1px solid #fde68a; }
  .dot { width: 6px; height: 6px; background: currentColor; border-radius: 50%; }

  .side-menu { padding: 20px; }
  .menu-group { margin-bottom: 24px; }
  .menu-group:last-child { margin-bottom: 0; }
  .menu-title { font-size: 11px; font-weight: 700; color: #64748b; letter-spacing: 0.5px; margin-bottom: 10px; padding-left: 12px; text-transform: uppercase; }
  
  .menu-item { 
    width: 100%; display: flex; align-items: center; gap: 12px; 
    padding: 10px 12px; background: transparent; border: none; border-radius: 8px; 
    color: #475569; font-size: 14px; font-weight: 500; cursor: pointer; text-align: left; 
    transition: all 0.2s;
  }
  .menu-item:hover { background: #f4f9fd; color: #1880c2; }
  .menu-item.active { background: #e0f2fe; color: #1880c2; font-weight: 600; border-left: 3px solid #1880c2; border-radius: 0 8px 8px 0; }
  
  .link-item { color: #334155; }
  .menu-arrow { margin-left: auto; color: #94a3b8; transition: 0.2s; }
  .menu-item:hover .menu-arrow { color: #1880c2; transform: translateX(3px); }

  .main-panel { overflow: hidden; }
  .panel-header { padding: 24px 32px; border-bottom: 1px solid #e0f2fe; background: white; }
  .panel-title { font-size: 20px; font-weight: 700; margin: 0; color: #1a202c; }
  .panel-desc { font-size: 13px; color: #64748b; margin: 6px 0 0; }
  
  .panel-body { padding: 32px; background: white; }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  
  .input-group { display: flex; flex-direction: column; gap: 8px; }
  .input-group.full { grid-column: 1 / -1; }
  label { font-size: 13px; font-weight: 600; color: #334155; }
  
  input { 
    padding: 10px 14px; 
    border: 1px solid #cbd5e1; 
    border-radius: 8px; 
    font-size: 14px; 
    outline: none; 
    transition: all 0.2s;
    background: #ffffff;
    color: #1a202c;
  }
  input:focus { border-color: #1880c2; box-shadow: 0 0 0 3px rgba(24, 128, 194, 0.15); }
  input.disabled { background: #f8fafc; color: #94a3b8; border-color: #e2e8f0; cursor: not-allowed; }
  
  .info-box.warning { 
    background: #fffbeb; border: 1px solid #fde68a; color: #b45309; 
    padding: 12px 16px; border-radius: 8px; display: flex; gap: 12px; margin-bottom: 8px; 
    font-size: 13px;
  }

  .panel-footer { margin-top: 32px; padding-top: 24px; border-top: 1px solid #e0f2fe; display: flex; justify-content: flex-end; }
  
  .btn-save { 
    background: #1880c2; color: white; border: none; 
    padding: 10px 24px; border-radius: 8px; font-weight: 600; 
    cursor: pointer; transition: 0.2s;
    box-shadow: 0 4px 6px rgba(24, 128, 194, 0.2);
  }
  .btn-save:hover:not(:disabled) { background: #0ea5e9; box-shadow: 0 6px 12px rgba(24, 128, 194, 0.3); }
  .btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

  .hint { font-size: 12px; color: #94a3b8; }

  @media (max-width: 900px) { 
    .content-wrapper { grid-template-columns: 1fr; margin-top: -60px; } 
    .form-grid { grid-template-columns: 1fr; }
    .panel-header { padding: 20px; }
    .panel-body { padding: 20px; }
  }
`;